<!-- eslint-disable
  vuejs-accessibility/mouse-events-have-key-events,
  vuejs-accessibility/no-static-element-interactions,
  vuejs-accessibility/anchor-has-content
-->
<template>
  <div
    class="product-card grid"
    :class="{
      [$class('loading')]: !product,
      'show-secondary-image': showSecondaryImage,
    }"
    @mouseenter="loadSecondaryImage = true"
  >
    <a
      class="product-card__image-container col xs2 l3"
      :href="product.url"
      @focus="loadSecondaryImage = true"
    >
      <responsive-image
        class="product-card__primary-image"
        :image="product.featuredImage"
        min-max="200-500"
        ratio="1:1"
        sizes="(min-width: 1140px) 25vw, 50vw"
      />

      <responsive-image
        v-if="loadSecondaryImage && product.secondaryImage"
        class="product-card__secondary-image"
        :image="product.secondaryImage"
        min-max="200-500"
        :padding="false"
        ratio="1:1"
        sizes="(min-width: 1140px) 25vw, 50vw"
        @image-loaded="showSecondaryImage = true"
      />

      <span
        v-if="!product.featuredImage?.src"
        class="visually-hidden"
        v-text="imageTextFallback"
      />

      <content-badge
        v-if="badgeText"
        :badge-text="badgeText"
        color-scheme="grey"
      >
        <template #icon>
          <icon-bell-fill />
        </template>
      </content-badge>
    </a>

    <div class="product-card__footer col xs4 l4-12">
      <product-vendor
        class="product-card__vendor"
        :product="product"
      />

      <component
        :is="product.url ? 'a' : 'p'"
        class="
          product-card__title
          text-heading-m
          text-heading-l-desktop
        "
        :href="product.url"
      >
        <h3 v-text="product.title" />
      </component>

      <div
        v-if="reviews && $variable('settings.reviewsIo.enable')"
        class="product-card__rating"
        :class="{ [$class('hidden')]: hideRatingWidget }"
      >
        <div
          :key="product.handle"
          class="ruk_rating_snippet"
          :class="`ruk_rating_snippet-${product?.handle}`"
          :data-sku="reviewsIoSku"
        />
      </div>

      <product-prices
        class="product-card__prices"
        :available="product?.available"
        :compare-at-price="product?.compareAtPrice?.amount"
        :price="product?.price?.amount"
        :varies="product?.price?.varies"
      />
    </div>

    <ul class="product-card__pills col xs-span l4-12">
      <template
        v-for="(type, index) of productType?.values"
        :key="`type-${index}`"
      >
        <li
          v-if="shouldShowPill(type, index)"
          class="product-card__pill"
        >
          <a
            class="product-card__pill-link text-body-s-regular"
            :href="product.url"
          >
            <component :is="getPillIcon(type)" />
            <span v-text="getPillText(type)" />
          </a>
        </li>
      </template>

      <li class="product-card__pill product-card__pill--vehicle-details-list">
        <vehicle-details-list
          class="product-card__vehicle-details-list"
          :product="product"
        />
      </li>
    </ul>

    <div class="product-card__actions col xs-span">
      <btn
        class="product-card__cta"
        :label="$string('product.cta.view_product')"
        modifiers="secondary center block"
        :url="product.url"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product card (product-card)
 * -----------------------------------------------------------------------------
 * Product card used throughout the site.
 *
 * @param {Boolean|Object} product - Product object, false shows loading state.
 * @param {Boolean} [reviews] - Show reviews widget.
 *
 */
import Btn from '~global/btn/btn.vue'
import ContentBadge from '~async/content-badge/content-badge'
import ProductPrices from '~global/product-prices/product-prices'
import ProductVendor from '~async/product-vendor/product-vendor'
import ResponsiveImage from '~global/images/responsive-image'
import VehicleDetailsList from '~async/vehicle-details-list/vehicle-details-list'

import IconBellFill from '~icons/general/bell-fill.svg'
import IconBundle from '~icons/general/bundle.svg'
import IconManual from '~icons/general/manual.svg'
import IconPhone from '~icons/general/phone.svg'

export default {
  name: 'ProductCard',

  components: {
    Btn,
    ContentBadge,
    IconBellFill,
    IconBundle,
    IconManual,
    IconPhone,
    ProductPrices,
    ProductVendor,
    ResponsiveImage,
    VehicleDetailsList,
  },

  props: {
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    reviews: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      hideRatingWidget: true,
      isB2b: cnvs.customer.isB2b,
      loadSecondaryImage: false,
      originalLiquidB2bData: null,
      pills: {
        bundle: this.$variable('settings.productOptions.bundle'),
        bundleDownload: this.$variable('settings.productOptions.bundleDownload'),
        digital: this.$variable('settings.productOptions.digital'),
        download: this.$variable('settings.productOptions.download'),
        physical: this.$variable('settings.productOptions.physical'),
      },
      showSecondaryImage: false,
    }
  },

  computed: {

    /**
     * Compute badge text.
     * @returns {String}
     */
    badgeText() {
      if (!this.product) {
        return ''
      }

      return this.product.available ? '' : this.$string('product.prices.sold_out')
    },

    /**
     * Compute product image text fallback - not alt tag just in case an
     * image isn't uploaded.
     * @returns {String}
     */
    imageTextFallback() {
      if (this.product.featuredImage?.src) {
        return ''
      }

      return this.$string('accessibility.product_image_fallback')
        .replace('{{ title }}', this.product.title)
    },

    /**
     * Compute product type from available product options.
     * @returns {Object|undefined}
     */
    productType() {
      const optionObject = this.product?.options?.find((option) => {
        return option.name === 'Type' || option.name === 'Title'
      })

      if (!optionObject) {
        return {}
      }

      const variants = optionObject.values?.map((value) => {
        const match = this.product.variants?.find((variant) => {
          const typeOption = variant.selectedOptions.find((selectedOption) => {
            return selectedOption.name === 'Type' || selectedOption.name === 'Title'
          })

          return typeOption.value === value
        })

        return match
      })

      const result = {
        ...optionObject,
        variants,
      }
      return result
    },

    /**
     * Compute Reviews.io SKU data attribute.
     * @returns {String}
     */
    reviewsIoSku() {
      if (
        !this.$variable('settings.reviewsIo.enable') ||
        !this.product ||
        !this.product?.variants?.length
      ) {
        return ''
      }

      const productType = this.product?.type.toLowerCase()
      if (productType === 'autofix') {
        return ''
      }

      const skus = this.product?.variants?.map((variant) => variant.sku)
      const ids = this.product?.variants?.map((variant) => variant.id)

      if (!skus.length || !ids.length) {
        return ''
      }

      return `${this.product.handle};${skus?.join(';')};${ids?.join(';')}`
    },
  },

  watch: {

    /**
     * Watch for changes to the product prop -
     *  this might be where data gets mutated
     */
    product: {
      deep: true,
      handler(newProduct) {
        if (newProduct?.handle) {
          // Product prop changed - recapture B2B data if needed
          this.captureOriginalLiquidB2bData()
        }
      },
    },

    /**
     * Watch Reviews.io SKU attribute to emit load event.
     * - Wait so that search results are rendered before running ratingSnippet.
     */
    reviewsIoSku: {
      async handler() {
        if (!this.reviewsIoSku) {
          return
        }

        await cnvs.Sleep(300)

        window.ratingSnippet(`ruk_rating_snippet-${this.product?.handle}`, {
          store: this.$variable('settings.reviewsIo.store'),
          mode: this.$variable('settings.reviewsIo.mode'),
          color: this.$variable('settings.reviewsIo.color'),
          linebreak: this.$variable('settings.reviewsIo.linebreak'),
          text: this.$variable('settings.reviewsIo.text'),
          singularText: this.$variable('settings.reviewsIo.singularText'),
          lang: this.$variable('settings.reviewsIo.lang'),
          usePolaris: this.$variable('settings.reviewsIo.usePolaris'),
          showEmptyStars: this.$variable('settings.reviewsIo.showEmptyStars'),
          showNumericalAverageRating: this.$variable('settings.reviewsIo.showNumericalAverageRating'),
        })

        this.hideRatingWidget = false
      },
      immediate: true,
    },
  },

  created() {
    // Capture the original Liquid B2B data on component creation
    this.captureOriginalLiquidB2bData()
  },

  methods: {

    /**
     * Capture the original Liquid B2B data before GraphQL mutations
     */
    captureOriginalLiquidB2bData() {
      const typeOption = this.product?.options?.find((option) => {
        return option.name === 'Type' || option.name === 'Title'
      })

      if (typeOption?.isB2bSaleable && typeOption.liquid) {
        // Deep clone to prevent mutation of captured data
        // Use JSON parse/stringify for true immutability
        this.originalLiquidB2bData = JSON.parse(JSON.stringify(typeOption.isB2bSaleable))
      }
    },

    /**
     * Return product option icon.
     * @param {String} type - Product type option.
     * @returns {VueComponent}
     */
    getPillIcon(type) {
      let icon

      if (this.pills.bundle.includes(type)) {
        icon = IconBundle
      } else if (this.pills.bundleDownload.includes(type)) {
        icon = IconBundle
      } else if (this.pills.physical.includes(type)) {
        icon = IconManual
      } else if (this.pills.digital.includes(type)) {
        icon = IconPhone
      } else if (this.pills.download.includes(type)) {
        icon = IconPhone
      }

      return icon
    },

    /**
     * Return product option text.
     * @param {String} type - Product type option.
     * @returns {String}
     */
    getPillText(type) {
      let text

      if (this.pills.bundle.includes(type)) {
        text = this.$string('product.options.card.bundle')
      } else if (this.pills.bundleDownload.includes(type)) {
        text = this.$string('product.options.card.bundle_download')
      } else if (this.pills.physical.includes(type)) {
        text = this.$string('product.options.card.print')
      } else if (this.pills.digital.includes(type)) {
        text = this.$string('product.options.card.online')
      } else if (this.pills.download.includes(type)) {
        text = this.$string('product.options.card.download')
      }

      return text
    },

    /**
     * Debug method to log pill rendering decisions
     */
    shouldShowPill(type, index) {
      const hasText = Boolean(this.getPillText(type))
      const isOnSale = this.productType?.isOnSale?.[index] === 'true'
      const isB2bAllowed = this.isB2bSaleable(index)
      const shouldShow = hasText && isOnSale && isB2bAllowed

      return shouldShow
    },

    /**
     * Check if a variant type is B2B saleable.
     * Use the pre-filtered isB2bSaleable array from Liquid format-option.liquid
     * This array was created server-side with exact same logic as
     * product-card.snippet.liquid
     * @param {Number} index - The index of the variant type.
     * @returns {Boolean}
     */
    isB2bSaleable(index) {
      if (!this.isB2b) {
        return true
      }

      // Get the variant type at this index
      if (!this.productType?.values?.[index]) {
        return true
      }

      // Use the pre-filtered isB2bSaleable array from Liquid
      // Prefer original Liquid data over potentially mutated GraphQL data
      const isB2bSaleableArray = this.originalLiquidB2bData ||
        this.productType?.isB2bSaleable
      const b2bSaleableValue = isB2bSaleableArray?.[index]

      // Liquid creates boolean values: true (allowed) or false (blocked)
      // But they might be converted to strings during JSON serialization
      const result = b2bSaleableValue !== false && b2bSaleableValue !== 'false'

      return result
    },
  },
}
</script>

<style lang="scss">
@import './product-card';
</style>
